import { getMyProfileApiUsersMeGet } from '@/api/sdk.gen'
import { useMutation, useQuery } from '@tanstack/react-query'
import queryClient from './queryClient'

// Token management
const TOKEN_KEY = 'kinzship_access_token'
const REFRESH_TOKEN_KEY = 'kinzship_refresh_token'

export const getStoredToken = () => localStorage.getItem(TOKEN_KEY)
export const getStoredRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY)

export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(TOKEN_KEY, accessToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

// Check if user is authenticated (has valid token)
export const isAuthenticated = () => {
  const token = getStoredToken()
  return !!token
}

// Hook to get current session
export const useGetSessionQuery = () => {
  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const token = getStoredToken()

      if (!token) {
        return null
      }

      try {
        // Get user profile from backend
        const response = await getMyProfileApiUsersMeGet({
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.data) {
          throw new Error('No user data returned')
        }

        return {
          session: {
            access_token: token,
            refresh_token: getStoredRefreshToken(),
            user: {
              id: response.data.id,
              email: response.data.email,
              user_metadata: {
                display_name: response.data.display_name,
              },
              created_at: response.data.created_at,
            },
          },
          user: {
            id: response.data.id,
            email: response.data.email,
            user_metadata: {
              display_name: response.data.display_name,
            },
            created_at: response.data.created_at,
          },
        }
      } catch (error) {
        // If token is invalid, clear it
        clearTokens()
        throw error
      }
    },
    enabled: isAuthenticated(), // Only run if we have a token
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

// Hook to logout
export const useLogoutMutation = () => {
  return useMutation({
    mutationFn: async () => {
      // Clear tokens and invalidate session
      clearTokens()
      queryClient.clear()
    },
    onSuccess: () => {
      // Redirect to login or home page
      window.location.href = '/login'
    },
  })
}
